import { useMemo, useState } from "react";
import { BarChart3, X } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { useGetLogbookAnalysisSheetQuery } from "../api/logbookApi";

type CellVal = { days: number; hours: number };
type SubjectRow = {
  id: number | null;
  code: string;
  description: string;
  total_days: number;
  total_hours: number;
};
type Metric = "days" | "hours";

const PALETTE = [
  "#4f46e5", "#7c3aed", "#0891b2", "#059669", "#d97706",
  "#dc2626", "#db2777", "#2563eb", "#65a30d", "#0d9488",
];

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

/**
 * Visual graphs for the analysis data: occurrence of each subject (how many
 * days / hours were logged against it) and the spread of activity across the
 * logbook's weeks. Opens in a responsive modal beside the analysis sheet.
 */
export default function LogbookAnalysisGraphs({
  logbookId,
}: {
  logbookId: string;
}) {
  const [open, setOpen] = useState(false);
  const [metric, setMetric] = useState<Metric>("days");

  const { data, isLoading, isError } = useGetLogbookAnalysisSheetQuery(
    { logbook_id: logbookId },
    { skip: !logbookId || !open }
  );

  const subjects: SubjectRow[] = data?.subjects ?? [];
  const weeks: number[] = data?.weeks ?? [];
  const columnTotals: Record<string, CellVal> = data?.column_totals ?? {};

  const val = (s: SubjectRow) =>
    metric === "days" ? s.total_days : s.total_hours;

  // Subjects grouped by competency area, sorted by occurrence (descending).
  // Codes look like TCD/A5, TCD/A6, TCD/B1 … — grouping strips the trailing
  // number so TCD/A5..A7 roll up to TCD/A, TCD/B1..B2 to TCD/B, and so on.
  const subjectData = useMemo(() => {
    const groups = new Map<string, { code: string; value: number; members: string[] }>();
    for (const s of subjects) {
      const key = String(s.code || "")
        .toUpperCase()
        .trim()
        .replace(/\d+$/, "") || "—";
      const g = groups.get(key) || { code: key, value: 0, members: [] };
      g.value += val(s);
      g.members.push(s.code);
      groups.set(key, g);
    }
    return Array.from(groups.values())
      .map((g) => ({
        code: g.code,
        name:
          g.members.length > 1
            ? `${g.code} (${g.members.length} sub-areas)`
            : g.members[0] || g.code,
        value: g.value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [subjects, metric]);

  const weekData = useMemo(
    () =>
      weeks.map((w) => ({
        week: `W${w}`,
        value:
          metric === "days"
            ? columnTotals?.[String(w)]?.days ?? 0
            : columnTotals?.[String(w)]?.hours ?? 0,
      })),
    [weeks, columnTotals, metric]
  );

  // Height scales with subject count so labels don't crush together.
  const subjectChartHeight = Math.max(240, subjectData.length * 38);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border bg-white hover:border-indigo-300 hover:shadow-sm transition text-left"
      >
        <span className="flex items-center gap-2 font-bold text-sm text-indigo-900">
          <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600">
            <BarChart3 size={16} />
          </span>
          Graphs
          <span className="hidden sm:inline text-xs font-normal text-gray-400">
            subject occurrence &amp; weekly spread
          </span>
        </span>
        <span className="text-xs font-semibold text-indigo-600">Open →</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-5xl sm:rounded-2xl shadow-xl flex flex-col max-h-full sm:max-h-[92vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b">
              <div className="flex items-center gap-2 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-lg bg-indigo-600 text-white shrink-0">
                  <BarChart3 size={16} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-black text-indigo-900 leading-tight truncate">
                    Analysis Graphs
                  </h3>
                  <p className="text-[11px] text-gray-400 leading-tight">
                    Subject occurrence &amp; weekly activity
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border overflow-hidden text-xs font-semibold">
                  {(["days", "hours"] as Metric[]).map((m) => (
                    <button
                      key={m}
                      onClick={() => setMetric(m)}
                      className={`px-3 py-1.5 capitalize ${
                        metric === m
                          ? "bg-indigo-600 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="grid place-items-center w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto p-4 sm:p-5 space-y-6">
              {isLoading && (
                <p className="text-sm text-gray-500 p-6">Loading graphs…</p>
              )}
              {isError && (
                <p className="text-sm text-red-600 p-6">Failed to load graph data.</p>
              )}

              {!isLoading && !isError && subjectData.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-10">
                  No entries logged yet — nothing to plot.
                </p>
              )}

              {!isLoading && !isError && subjectData.length > 0 && (
                <>
                  {/* Subject occurrence */}
                  <section>
                    <h4 className="text-sm font-bold text-gray-800 mb-1">
                      Occurrence by competency area
                    </h4>
                    <p className="text-xs text-gray-400 mb-3">
                      {metric === "days" ? "Days" : "Hours"} logged per competency
                      area — sub-codes are grouped (e.g. TCD/A5, TCD/A6 → TCD/A).
                    </p>
                    <div style={{ width: "100%", height: subjectChartHeight }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          layout="vertical"
                          data={subjectData}
                          margin={{ top: 4, right: 24, bottom: 4, left: 8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                          <XAxis type="number" allowDecimals={false} fontSize={11} />
                          <YAxis
                            type="category"
                            dataKey="code"
                            width={64}
                            fontSize={11}
                          />
                          <Tooltip content={<SubjectTip metric={metric} />} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={26}>
                            {subjectData.map((_, i) => (
                              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </section>

                  {/* Weekly spread */}
                  <section>
                    <h4 className="text-sm font-bold text-gray-800 mb-1">
                      Activity by week
                    </h4>
                    <p className="text-xs text-gray-400 mb-3">
                      Total {metric} logged in each week of the logbook.
                    </p>
                    <div style={{ width: "100%", height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={weekData}
                          margin={{ top: 4, right: 12, bottom: 4, left: -12 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="week"
                            fontSize={10}
                            interval={weekData.length > 26 ? 1 : 0}
                          />
                          <YAxis allowDecimals={false} fontSize={11} />
                          <Tooltip
                            formatter={(v: any) => [fmt(Number(v)), metric]}
                          />
                          <Bar
                            dataKey="value"
                            fill="#4f46e5"
                            radius={[4, 4, 0, 0]}
                            maxBarSize={22}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </section>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SubjectTip({
  active,
  payload,
  metric,
}: {
  active?: boolean;
  payload?: any[];
  metric: Metric;
}) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border rounded-lg shadow px-3 py-2 text-xs max-w-[240px]">
      <div className="font-semibold text-gray-800">{p.name}</div>
      <div className="text-indigo-700 font-bold mt-0.5">
        {fmt(p.value)} {metric}
      </div>
    </div>
  );
}
